import { ChevronsUpDown, LogOut, User } from 'lucide-react';
import { useEffect, useId, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useAuth } from '@/auth/AuthProvider';
import { type GuidePlatform, InstallAppItem, InstallGuide } from '@/pwa/InstallAppItem';

/**
 * Closes on a click anywhere outside, and on `Escape` from anywhere within. Both listeners are
 * attached only while open, so a closed disclosure costs nothing. `pointerdown` rather than
 * `click`: a `click` listener would fire on the same event that opened the panel in some
 * browsers' event ordering and close it again immediately.
 */
function useDismissOutside(
  open: boolean,
  setOpen: (open: boolean) => void,
  containerRef: RefObject<HTMLDivElement | null>,
  triggerRef: RefObject<HTMLButtonElement | null>,
) {
  useEffect(() => {
    if (!open) {
      return;
    }

    function handlePointerDown(event: PointerEvent) {
      if (
        event.target instanceof Node &&
        containerRef.current !== null &&
        !containerRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
        // Focus goes back to the trigger, not wherever it happened to be: the person pressed
        // Escape to leave the panel, and leaving it should not lose their place in the rail.
        triggerRef.current?.focus();
      }
    }

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, setOpen, containerRef, triggerRef]);
}

/**
 * The account disclosure at the rail's foot — where decision 5 sends the header's session group.
 *
 * WHY A DISCLOSURE AND NOT A MENU. This is a short list of LINKS plus one action, which is the WAI-
 * ARIA Authoring Practices' own "Disclosure Navigation" pattern: a button with `aria-expanded`
 * controlling a region, `Escape` to close, focus returned to the trigger. The `menu`/`menuitem`
 * roles are for application menus and bring an arrow-key focus model, type-ahead, and a focus trap
 * that a person expects to behave like a desktop menu bar — machinery whose absence is worse than
 * its presence, so half-implementing it is not an option.
 *
 * WHY NO LIBRARY. Radix ships a `DropdownMenu` and it is not installed here. Adding a dependency
 * to carry two links and a button is the wrong trade for this project: the Brand Kit's
 * § Iconography already records that a new package needs explicit Human approval, and this
 * codebase already contains the exact focus-restoration pattern the disclosure needs
 * (`NavigationDrawer.tsx` had to write it by hand because Radix's own restoration silently no-ops
 * for an externally controlled dialog). Forty lines here beat a package and its transitive
 * advisories in the audit register.
 *
 * ADMINISTRATION IS NOT HERE, and that is a deliberate divergence from decision 5's own wording.
 * The proposal's prose lists "profile, administration and sign-out" in this menu while its mockup
 * shows Amministrazione as a rail MODULE — the two disagree, and the module is right: it is a
 * routed section with its own gate and its own screens, exactly like Finance. Naming it in both
 * places would break the guidelines' "nothing named twice on the same screen".
 */
export function AccountMenu() {
  const { user, signOutCurrentUser } = useAuth();
  const { t } = useTranslation('shell');
  const [open, setOpen] = useState(false);
  const [guide, setGuide] = useState<GuidePlatform | null>(null);
  const panelId = useId();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useDismissOutside(open, setOpen, containerRef, triggerRef);

  const email = user?.email ?? '';
  // Two letters from the address, uppercased: the platform has no display-name read at the shell
  // level, and two letters are the standard's own accepted "distinguishing mark".
  const initials = email.slice(0, 2).toLocaleUpperCase('it');

  return (
    <div className="rail-foot" ref={containerRef}>
      {open ? (
        <div className="account-panel" id={panelId}>
          <Link className="account-item" to="/profile" onClick={() => setOpen(false)}>
            <User aria-hidden="true" />
            {t('nav.profile')}
          </Link>
          <InstallAppItem onDone={() => setOpen(false)} onOpenGuide={setGuide} />
          <button
            className="account-item"
            type="button"
            onClick={() => {
              setOpen(false);
              void signOutCurrentUser();
            }}
          >
            <LogOut aria-hidden="true" />
            {t('signOut')}
          </button>
        </div>
      ) : null}
      <button
        className="account-trigger"
        type="button"
        ref={triggerRef}
        aria-expanded={open}
        aria-controls={panelId}
        // The email is the accessible name at every width. In the compressed rail the identity
        // block is `display: none`, so without this the trigger would be an unnamed button holding
        // two letters.
        aria-label={t('account.menuLabel', { email })}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="account-avatar" aria-hidden="true">
          {initials}
        </span>
        <span className="account-identity">
          <span className="account-mail">{email}</span>
        </span>
        <ChevronsUpDown aria-hidden="true" className="account-chevron size-3.5" />
      </button>
      {/* Outside the panel, which closes when the guide opens (see `InstallAppItem`). */}
      {guide !== null ? (
        <InstallGuide
          platform={guide}
          open
          onOpenChange={(next) => {
            if (!next) {
              setGuide(null);
            }
          }}
          returnFocusTo={triggerRef}
        />
      ) : null}
    </div>
  );
}
